import { useState } from "react";
import { siteData, HeroData, FeatureItem, CTAData, TestimonialItem } from "@/store/siteData";
import { saveImage, getImageUrl } from "@/utils/imageDB";

type SubTab = "hero" | "features" | "cta" | "testimonials";

function SaveBar({ onSave, saved }: { onSave: () => void; saved: boolean }) {
  return (
    <div className="flex justify-end pt-4 border-t border-[#2B3654]">
      <button onClick={onSave} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
        <div className="w-4 h-4 flex items-center justify-center"><i className={saved ? "ri-check-line" : "ri-save-line"}></i></div>
        {saved ? "Kaydedildi!" : "Kaydet"}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean }) {
  const cls = "w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-2.5 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]";
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className={cls + " resize-none"} />
        : <input type="text" value={value} onChange={e => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

// ─── Hero Editor ─────────────────────────────────────────────────────────────
function HeroEditor() {
  const [data, setData] = useState<HeroData>(siteData.getHero());
  const [saved, setSaved] = useState(false);
  const upd = (key: keyof HeroData, val: string) => { setData(p => ({ ...p, [key]: val })); setSaved(false); };
  const updStat = (i: number, key: "value" | "label" | "icon", val: string) => {
    const stats = [...data.stats];
    stats[i] = { ...stats[i], [key]: val };
    setData(p => ({ ...p, stats }));
    setSaved(false);
  };
  const handleSave = () => { siteData.saveHero(data); setSaved(true); setTimeout(() => setSaved(false), 3000); };

  return (
    <div className="space-y-5">
      <h3 className="text-white font-semibold pb-3 border-b border-[#2B3654]">Hero Bölümü</h3>
      <Field label="Etiket Metni (küçük üst yazı)" value={data.tagline} onChange={v => upd("tagline", v)} />
      <Field label="Ana Başlık" value={data.title} onChange={v => upd("title", v)} />
      <Field label="Alt Metin (Açıklama)" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="1. Buton Metni" value={data.cta1Text} onChange={v => upd("cta1Text", v)} />
        <Field label="2. Buton Metni" value={data.cta2Text} onChange={v => upd("cta2Text", v)} />
      </div>
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-3">İstatistik Çubuğu (3 kutu)</p>
        <div className="space-y-3">
          {data.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-3 gap-3 bg-[#0F1729] p-3 rounded-lg border border-[#2B3654]">
              <Field label={`${i + 1}. Değer`} value={s.value} onChange={v => updStat(i, "value", v)} />
              <Field label="Etiket" value={s.label} onChange={v => updStat(i, "label", v)} />
              <Field label="İkon Adı" value={s.icon} onChange={v => updStat(i, "icon", v)} />
            </div>
          ))}
        </div>
      </div>
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ─── Features Editor ──────────────────────────────────────────────────────────
function FeaturesEditor() {
  const [features, setFeatures] = useState<FeatureItem[]>(siteData.getFeatures());
  const [saved, setSaved] = useState(false);
  const [previews, setPreviews] = useState<Record<number, string>>({});

  const upd = (id: number, key: keyof FeatureItem, val: string) => {
    setFeatures(p => p.map(f => f.id === id ? { ...f, [key]: val } : f));
    setSaved(false);
  };

  const handleImageUpload = async (id: number, file: File) => {
    const imgId = await saveImage(file);
    const url = await getImageUrl(imgId);
    setFeatures(p => p.map(f => f.id === id ? { ...f, imageId: imgId } : f));
    if (url) setPreviews(p => ({ ...p, [id]: url }));
    setSaved(false);
  };

  const handleSave = () => { siteData.saveFeatures(features); setSaved(true); setTimeout(() => setSaved(false), 3000); };

  return (
    <div className="space-y-5">
      <h3 className="text-white font-semibold pb-3 border-b border-[#2B3654]">Özellikler Bölümü (3 kart)</h3>
      {features.map((feat) => (
        <div key={feat.id} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[#3B82F6] font-bold text-sm">#{feat.id}</span>
            <span className="text-white font-medium text-sm">{feat.title}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Başlık" value={feat.title} onChange={v => upd(feat.id, "title", v)} />
            <Field label="İkon Kodu (örn: ri-tools-line)" value={feat.icon} onChange={v => upd(feat.id, "icon", v)} />
          </div>
          <Field label="Açıklama" value={feat.desc} onChange={v => upd(feat.id, "desc", v)} multiline />
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Görsel (PNG/JPG yükle)</label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-16 flex-shrink-0 rounded overflow-hidden border border-[#2B3654]">
                <img src={previews[feat.id] || feat.imageUrl} alt="" className="w-full h-full object-cover" />
              </div>
              <label className="flex-1 flex items-center gap-2 cursor-pointer border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-4 py-3 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line text-[#3B82F6]"></i></div>
                <span className="text-[#7B8FAF] text-xs">PNG, JPG yükle {feat.imageId ? "(yüklendi ✓)" : ""}</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImageUpload(feat.id, e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ─── CTA Editor ───────────────────────────────────────────────────────────────
function CTAEditor() {
  const [data, setData] = useState<CTAData>(siteData.getCTA());
  const [saved, setSaved] = useState(false);
  const upd = (key: keyof CTAData, val: string) => { setData(p => ({ ...p, [key]: val })); setSaved(false); };
  const updTrust = (i: number, key: "icon" | "text", val: string) => {
    const items = [...data.trustItems];
    items[i] = { ...items[i], [key]: val };
    setData(p => ({ ...p, trustItems: items }));
    setSaved(false);
  };
  const addTrust = () => { setData(p => ({ ...p, trustItems: [...p.trustItems, { icon: "ri-star-line", text: "Yeni Özellik" }] })); setSaved(false); };
  const removeTrust = (i: number) => { setData(p => ({ ...p, trustItems: p.trustItems.filter((_, idx) => idx !== i) })); setSaved(false); };
  const handleSave = () => { siteData.saveCTA(data); setSaved(true); setTimeout(() => setSaved(false), 3000); };

  return (
    <div className="space-y-5">
      <h3 className="text-white font-semibold pb-3 border-b border-[#2B3654]">CTA / Çağrı Bölümü</h3>
      <Field label="Etiket (küçük üst metin)" value={data.tag} onChange={v => upd("tag", v)} />
      <Field label="Ana Başlık" value={data.title} onChange={v => upd("title", v)} multiline />
      <Field label="Alt Açıklama" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="1. Buton (Randevu)" value={data.cta1Text} onChange={v => upd("cta1Text", v)} />
        <Field label="2. Buton (Telefon)" value={data.cta2Text} onChange={v => upd("cta2Text", v)} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Güven Göstergeleri</p>
          <button onClick={addTrust} className="text-xs text-[#B8C5D8] border border-[#2B3654] px-3 py-1 rounded hover:border-[#3B82F6] cursor-pointer whitespace-nowrap">+ Ekle</button>
        </div>
        <div className="space-y-2">
          {data.trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#0F1729] p-3 rounded-lg border border-[#2B3654]">
              <input type="text" value={item.icon} onChange={e => updTrust(i, "icon", e.target.value)} placeholder="ikon adı" className="w-36 bg-[#141E35] border border-[#2B3654] text-white text-xs rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className={`${item.icon} text-[#3B82F6]`}></i></div>
              <input type="text" value={item.text} onChange={e => updTrust(i, "text", e.target.value)} className="flex-1 bg-[#141E35] border border-[#2B3654] text-white text-sm rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
              <button onClick={() => removeTrust(i)} className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300 cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line text-sm"></i></button>
            </div>
          ))}
        </div>
      </div>
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ─── Testimonials Editor ──────────────────────────────────────────────────────
function TestimonialsEditor() {
  const [items, setItems] = useState<TestimonialItem[]>(siteData.getTestimonials());
  const [saved, setSaved] = useState(false);
  const [previews, setPreviews] = useState<Record<number, string>>({});

  const upd = (id: number, key: keyof TestimonialItem, val: string) => { setItems(p => p.map(t => t.id === id ? { ...t, [key]: val } : t)); setSaved(false); };
  const handleImageUpload = async (id: number, file: File) => {
    const imgId = await saveImage(file);
    const url = await getImageUrl(imgId);
    setItems(p => p.map(t => t.id === id ? { ...t, imageId: imgId } : t));
    if (url) setPreviews(p => ({ ...p, [id]: url }));
    setSaved(false);
  };
  const addItem = () => {
    const newId = Math.max(...items.map(t => t.id), 0) + 1;
    setItems(p => [...p, { id: newId, quote: "Yeni referans metni", name: "Ad Soyad", title: "Unvan", company: "Şirket", imageUrl: "https://readdy.ai/api/search-image?query=professional%20portrait%20business%20person%20studio%20lighting&width=400&height=500&seq=newtest&orientation=portrait" }]);
    setSaved(false);
  };
  const removeItem = (id: number) => { setItems(p => p.filter(t => t.id !== id)); setSaved(false); };
  const handleSave = () => { siteData.saveTestimonials(items); setSaved(true); setTimeout(() => setSaved(false), 3000); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between pb-3 border-b border-[#2B3654]">
        <h3 className="text-white font-semibold">Referanslar</h3>
        <button onClick={addItem} className="text-xs text-[#B8C5D8] border border-[#2B3654] px-3 py-1.5 rounded hover:border-[#3B82F6] cursor-pointer whitespace-nowrap">+ Yeni Ekle</button>
      </div>
      {items.map((item) => (
        <div key={item.id} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-white font-medium text-sm">{item.name}</span>
            <button onClick={() => removeItem(item.id)} className="text-xs text-red-400 hover:text-red-300 cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-delete-bin-line"></i> Sil</button>
          </div>
          <Field label="Alıntı / Yorum" value={item.quote} onChange={v => upd(item.id, "quote", v)} multiline />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Ad Soyad" value={item.name} onChange={v => upd(item.id, "name", v)} />
            <Field label="Unvan" value={item.title} onChange={v => upd(item.id, "title", v)} />
            <Field label="Şirket" value={item.company} onChange={v => upd(item.id, "company", v)} />
          </div>
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Fotoğraf</label>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden border border-[#2B3654]">
                <img src={previews[item.id] || item.imageUrl} alt="" className="w-full h-full object-cover" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-4 py-2.5 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line text-[#3B82F6]"></i></div>
                <span className="text-[#7B8FAF] text-xs">Fotoğraf yükle {item.imageId ? "(✓)" : ""}</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImageUpload(item.id, e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomepageEditor() {
  const [subTab, setSubTab] = useState<SubTab>("hero");
  const subTabs: { id: SubTab; label: string; icon: string }[] = [
    { id: "hero", label: "Hero", icon: "ri-home-2-line" },
    { id: "features", label: "Özellikler", icon: "ri-star-line" },
    { id: "cta", label: "CTA Bölümü", icon: "ri-notification-2-line" },
    { id: "testimonials", label: "Referanslar", icon: "ri-message-2-line" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Ana Sayfa İçerikleri</h1>
        <p className="text-[#7B8FAF] text-sm mt-0.5">Sitedeki tüm ana sayfa yazılarını ve görsellerini düzenleyin</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${subTab === t.id ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40" : "text-[#7B8FAF] hover:text-white border border-[#2B3654]"}`}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.icon}></i></div>
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
        {subTab === "hero" && <HeroEditor />}
        {subTab === "features" && <FeaturesEditor />}
        {subTab === "cta" && <CTAEditor />}
        {subTab === "testimonials" && <TestimonialsEditor />}
      </div>
    </div>
  );
}
